/**
 * Vault gate: the full-pane password screen shown when the Vault view is
 * entered while locked — the web counterpart of `gateContent` in
 * VaultScreen.swift. Nothing about the vault (not even the note count) is
 * visible until the password is verified and the server unwraps the DEK.
 *
 * The password goes to the server, which checks it against the remote
 * verifier; neither the password nor the DEK is ever stored in the browser.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Vault } from 'lucide-react';
import { vaultApi } from '../lib/api/vault.js';
import { ApiError } from '../lib/api/client.js';
import { useOverlayScrollbar } from '../lib/ui/overlayScroll.js';

interface VaultGateProps {
  onUnlocked: () => void;
}

export function VaultGate({ onUnlocked }: VaultGateProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Floating capsule scrollbar (macOS overlay parity) — see lib/ui/overlayScroll.ts.
  const gateScroll = useOverlayScrollbar<HTMLDivElement>();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (submitting || password === '') return;
    setSubmitting(true);
    setError(null);
    try {
      await vaultApi.unlock(password);
      setPassword('');
      onUnlocked();
    } catch (caught) {
      if (caught instanceof ApiError && caught.isUnauthorized) {
        setError('Incorrect password.');
      } else if (caught instanceof ApiError && caught.code === 'vault_not_initialized') {
        setError('No vault found. Set one up in the Panda Note app first.');
      } else {
        setError(caught instanceof Error ? caught.message : 'Could not unlock the vault.');
      }
    } finally {
      setSubmitting(false);
      inputRef.current?.focus();
    }
  }

  return (
    <div className="ob-host ob-host-grow" ref={gateScroll.hostRef}>
      <div className="vault-gate" ref={gateScroll.scrollerRef}>
        <Vault size={36} strokeWidth={1.75} className="vault-gate-icon" aria-hidden="true" />
      <h1 className="vault-gate-title">Unlock Vault</h1>
      <p className="vault-gate-text">Enter your password to view and edit locked notes.</p>

      <form className="vault-gate-form" onSubmit={onSubmit}>
        <input
          ref={inputRef}
          className="input"
          type="password"
          value={password}
          autoComplete="off"
          placeholder="Password"
          aria-label="Vault password"
          disabled={submitting}
          onChange={(event) => setPassword(event.target.value)}
        />
        {error && (
          <div className="vault-gate-error" role="alert">
            {error}
          </div>
        )}
        <button type="submit" className="button vault-gate-button" disabled={submitting || password === ''}>
          {submitting ? 'Unlocking…' : 'Unlock'}
        </button>
      </form>
      </div>
    </div>
  );
}
